import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../config/supabase';
import type { Customer } from '../../config/supabase';

// Query Keys
export const customersKeys = {
  all: ['customers'] as const,
  lists: () => [...customersKeys.all, 'list'] as const,
  list: (page?: number, pageSize?: number, search?: string) => 
    [...customersKeys.lists(), { page, pageSize, search }] as const,
};

// Fetch all customers (for dropdowns)
export const useCustomersAll = () => {
  return useQuery({
    queryKey: customersKeys.list(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    },
    // Cache for 3 minutes
    staleTime: 3 * 60 * 1000,
  });
};

// Fetch customers with pagination
export const useCustomers = (page: number = 1, pageSize: number = 10, search: string = '') => {
  return useQuery({
    queryKey: customersKeys.list(page, pageSize, search),
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from('customers')
        .select('*', { count: 'exact' });

      // Apply search filter if search term exists
      if (search.trim()) {
        query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%,phone.ilike.%${search}%`);
      }

      query = query
        .order('name')
        .range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;
      
      return {
        customers: data || [],
        totalCount: count || 0,
      };
    },
    staleTime: 2 * 60 * 1000,
  });
};

// Create customer mutation
export const useCreateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (customerData: Omit<Customer, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('customers')
        .insert(customerData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customersKeys.all });
    },
  });
};

// Update customer mutation
export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Customer> & { id: string }) => {
      const { data, error } = await supabase
        .from('customers')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customersKeys.all });
    },
  });
};

// Delete customer mutation
export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customersKeys.all });
    },
  });
}; 